import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { transactionAPI } from '../services/api';
import { Search, DollarSign, TrendingUp, BarChart3, Calculator, Calendar, Eye, Edit, Package, FileText } from 'lucide-react';
import { formatDate, formatCurrency } from '../utils/formatters';

const Costs = () => {
    const navigate = useNavigate();
    const [filters, setFilters] = useState({
        page: 1,
        limit: 10,
        search: '',
        type: '',
        startDate: '',
        endDate: '',
    });
    const [searchInput, setSearchInput] = useState('');
    const [typeInput, setTypeInput] = useState('');
    const [startDateInput, setStartDateInput] = useState('');
    const [endDateInput, setEndDateInput] = useState('');

    const { data, isLoading, error } = useQuery({
        queryKey: ['costs', filters],
        queryFn: () => transactionAPI.getAll(filters),
        select: (response) => ({
            ...response.data.pagination,
                            data: response.data.transactions?.map(contract => ({
                    contractId: contract.id,
                    contractDate: contract.contract_date,
                    contractStatus: contract.status,
                    contractType: contract.type,
                    deceased: contract.deads?.[0] || null, // 첫 번째 사망자 정보 사용
                    mausoleum: contract.mausoleum, // 묘소 정보 추가
                    cost: contract.cost
                })).filter(item => item.cost) || []
        }),
        keepPreviousData: true,
    });

    const { data: stats } = useQuery({
        queryKey: ['cost-stats', filters.startDate, filters.endDate],
        queryFn: () => transactionAPI.getStatistics({
            startDate: filters.startDate,
            endDate: filters.endDate
        }),
        select: (response) => response.data,
    });

    // 새로운 API: 모든 비용 항목 조회 (비용, 석물, 각자 통합)
    const { data: allCostItems } = useQuery({
        queryKey: ['all-cost-items'],
        queryFn: async () => {
            // 모든 계약의 비용 항목을 조회
            const contracts = data?.data || [];
            const allItems = [];
            
            for (const contract of contracts) {
                try {
                    const response = await transactionAPI.getAllCostItems(contract.contractId);
                    const items = response.data.map(item => ({
                        ...item,
                        contractId: contract.contractId,
                        contractDate: contract.contractDate,
                        deceased: contract.deceased,
                        mausoleum: contract.mausoleum
                    }));
                    allItems.push(...items);
                } catch (error) {
                    console.error(`계약 ${contract.contractId}의 비용 항목 조회 실패:`, error);
                }
            }
            
            return allItems;
        },
        enabled: !!data?.data && data.data.length > 0,
    });

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }));
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const handleQuery = () => {
        setFilters(prev => ({
            ...prev,
            search: searchInput,
            type: typeInput,
            startDate: startDateInput,
            endDate: endDateInput,
            page: 1
        }));
    };

    const handleClearFilters = () => {
        setSearchInput('');
        setTypeInput('');
        setStartDateInput('');
        setEndDateInput('');
        setFilters(prev => ({
            ...prev,
            search: '',
            type: '',
            startDate: '',
            endDate: '',
            page: 1
        }));
    };

    useEffect(() => {
        setSearchInput(filters.search);
        setTypeInput(filters.type);
        setStartDateInput(filters.startDate);
        setEndDateInput(filters.endDate);
    }, []);

    // 비용 항목 타입에 따른 아이콘 반환
    const getCostItemIcon = (type) => {
        switch (type) {
            case 'cost':
                return <DollarSign size={16} className="text-blue-600" />;
            case 'stone':
                return <Package size={16} className="text-green-600" />;
            case 'engraving':
                return <FileText size={16} className="text-purple-600" />;
            default:
                return <DollarSign size={16} className="text-gray-600" />;
        }
    };

    // 비용 항목 타입에 따른 배경색 반환
    const getCostItemBgColor = (type) => {
        switch (type) {
            case 'cost':
                return 'bg-blue-50 border-blue-200';
            case 'stone':
                return 'bg-green-50 border-green-200';
            case 'engraving':
                return 'bg-purple-50 border-purple-200';
            default:
                return 'bg-gray-50 border-gray-200';
        }
    };

    if (isLoading) {
        return <div className="loading">비용 정보를 불러오는 중...</div>;
    }

    if (error) {
        return (
            <div className="error">
                비용 정보를 불러오는 중 오류가 발생했습니다: {error.message}
            </div>
        );
    }

    const costBreakdown = (cost) => {
        const items = [
            { label: '사용료', value: cost.usage_fee },
            { label: '관리비', value: cost.management_fee },
            { label: '매장작업비', value: cost.burial_work_fee },
            { label: '설치비', value: cost.installation_fee },
            { label: '보수비', value: cost.repair_fee },
            { label: '제거작업비', value: cost.removal_work_fee },
            { label: '기타작업비', value: cost.other_work_fee },
            { label: '석물가공비', value: cost.stone_processing_fee },
            { label: '대행수수료', value: cost.agency_fee },
            { label: '재사용료', value: cost.reuse_fee }
        ].filter(item => item.value > 0);

        return items;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case '완료': return 'status-completed';
            case '진행중': return 'status-progress';
            case '서류 대기': return 'status-waiting';
            default: return 'status-cancelled';
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case '신규': return 'status-progress';
            case '이장': return 'status-waiting';
            case '연장': return 'status-completed';
            default: return 'status-cancelled';
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900">비용 관리</h1>
                <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-500">
                        총 {data?.totalItems || 0}건의 계약
                    </div>
                </div>
            </div>

            {/* 비용 통계 */}
            {stats && (
                <div className="grid grid-4 mb-6" style={{ gap: '20px' }}>
                    <div className="card">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-sm text-gray-600 mb-2">총 수익</p>
                                <p className="text-2xl font-bold text-green-600">
                                    {formatCurrency(stats.totalRevenue)}
                                </p>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#d1fae5' }}>
                                <TrendingUp size={24} className="text-green-600" />
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-sm text-gray-600 mb-2">평균 계약 금액</p>
                                <p className="text-2xl font-bold text-blue-600">
                                    {formatCurrency(stats.averageCost)}
                                </p>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#dbeafe' }}>
                                <BarChart3 size={24} className="text-blue-600" />
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-sm text-gray-600 mb-2">신규 계약</p>
                                <p className="text-2xl font-bold text-purple-600">{stats.newContracts || 0}건</p>
                                <p className="text-sm text-gray-500">이번 달</p>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#e5e7eb' }}>
                                <DollarSign size={24} className="text-purple-600" />
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="flex justify-between items-center">
                            <div>
                                <p className="text-sm text-gray-600 mb-2">이장 계약</p>
                                <p className="text-2xl font-bold text-orange-600">{stats.relocationContracts || 0}건</p>
                                <p className="text-sm text-gray-500">이번 달</p>
                            </div>
                            <div style={{ padding: '12px', borderRadius: '8px', backgroundColor: '#fed7aa' }}>
                                <DollarSign size={24} className="text-orange-600" />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 검색 및 필터 */}
            <div className="card mb-6">
                <div className="grid grid-5" style={{ gap: '16px', alignItems: 'end' }}>
                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            검색
                        </label>
                        <div className="relative">
                            <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="사망자명 검색"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="input w-full pl-10"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            계약 구분
                        </label>
                        <select
                            value={typeInput}
                            onChange={(e) => setTypeInput(e.target.value)}
                            className="select w-full"
                        >
                            <option value="">전체</option>
                            <option value="신규">신규</option>
                            <option value="이장">이장</option>
                            <option value="연장">연장</option>
                            <option value="해지">해지</option>
                        </select>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            시작일
                        </label>
                        <div className="relative">
                            <Calendar size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="date"
                                value={startDateInput}
                                onChange={(e) => setStartDateInput(e.target.value)}
                                className="input w-full pl-10"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="text-sm font-medium text-gray-700 mb-1">
                            종료일
                        </label>
                        <div className="relative">
                            <Calendar size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="date"
                                value={endDateInput}
                                onChange={(e) => setEndDateInput(e.target.value)}
                                className="input w-full pl-10"
                            />
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
                            초기화
                        </button>
                        <button type="button" className="btn btn-primary" onClick={handleQuery}>조회</button>
                    </div>
                </div>
            </div>

            {/* 비용 목록 테이블 */}
            {data?.data?.length > 0 ? (
                <div className="card">
                    <div className="overflow-x-auto">
                        <table className="table w-full">
                            <thead>
                                <tr>
                                    <th>묘적번호</th>
                                    <th>사망자명</th>
                                    <th>계약일</th>
                                    <th>계약 구분</th>
                                    <th>총 계약 금액</th>
                                    <th>주요 비용 항목</th>
                                    <th>계약 상태</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.data.map((item) => (
                                    <tr key={item.contractId}>
                                        <td className="font-medium">
                                            {item.mausoleum?.member_number || '정보 없음'}
                                        </td>
                                        <td>
                                            {item.deceased?.name || '정보 없음'}
                                        </td>
                                        <td>
                                            {formatDate(item.contractDate)}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getTypeColor(item.contractType)}`}>
                                                {item.contractType}
                                            </span>
                                        </td>
                                        <td className="font-bold text-green-600">
                                            {formatCurrency(item.cost?.total_cost)}
                                        </td>
                                        <td>
                                            <div className="text-sm">
                                                {costBreakdown(item.cost).slice(0, 3).map((costItem, index) => (
                                                    <div key={index} className="flex justify-between">
                                                        <span className="text-gray-600">{costItem.label}:</span>
                                                        <span className="font-medium">{formatCurrency(costItem.value)}</span>
                                                    </div>
                                                ))}
                                                {costBreakdown(item.cost).length > 3 && (
                                                    <div className="text-xs text-gray-500 mt-1">
                                                        +{costBreakdown(item.cost).length - 3}개 항목
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`status-badge ${getStatusColor(item.contractStatus)}`}>
                                                {item.contractStatus}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => navigate(`/contracts/${item.contractId}`)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="상세 보기"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => navigate(`/contracts/edit/${item.contractId}`)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="비용 수정"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    {data?.totalPages > 1 && (
                        <div className="flex justify-between items-center mt-6">
                            <div className="text-sm text-gray-700">
                                총 {data.totalItems}개 중 {data.from}-{data.to}개
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(data.currentPage - 1)}
                                    disabled={data.currentPage <= 1}
                                    className="btn btn-secondary"
                                >
                                    이전
                                </button>
                                <span className="px-3 py-2 text-sm text-gray-700">
                                    {data.currentPage} / {data.totalPages}
                                </span>
                                <button
                                    onClick={() => handlePageChange(data.currentPage + 1)}
                                    disabled={data.currentPage >= data.totalPages}
                                    className="btn btn-secondary"
                                >
                                    다음
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="empty-state">
                    <DollarSign size={48} className="text-gray-300 mb-4" />
                    <p className="text-gray-500">비용 정보가 있는 계약이 없습니다.</p>
                </div>
            )}

            {/* 새로운 통합 비용 항목 테이블 */}
            {allCostItems && allCostItems.length > 0 && (
                <div className="card mt-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <Calculator size={20} className="text-blue-600" />
                        통합 비용 항목 상세
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="table w-full">
                            <thead>
                                <tr>
                                    <th>묘적번호</th>
                                    <th>사망자명</th>
                                    <th>계약일</th>
                                    <th>비용 항목</th>
                                    <th>금액</th>
                                    <th>납부 상태</th>
                                    <th>결제 방법</th>
                                    <th>결제 날짜</th>
                                    <th>관리</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allCostItems.map((item, index) => (
                                    <tr key={`${item.contractId}-${item.id}-${index}`}>
                                        <td className="font-medium">
                                            {item.mausoleum?.member_number || '정보 없음'}
                                        </td>
                                        <td>
                                            {item.deceased?.name || '정보 없음'}
                                        </td>
                                        <td>
                                            {formatDate(item.contractDate)}
                                        </td>
                                        <td>
                                            <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${getCostItemBgColor(item.type)}`}>
                                                {getCostItemIcon(item.type)}
                                                <span className="text-sm font-medium">{item.name}</span>
                                            </div>
                                        </td>
                                        <td className="font-bold text-green-600">
                                            {formatCurrency(item.fee)}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${item.fee_status === '납부' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {item.fee_status}
                                            </span>
                                        </td>
                                        <td>
                                            {item.payment_method || '-'}
                                        </td>
                                        <td>
                                            {item.payment_date ? formatDate(item.payment_date) : '-'}
                                        </td>
                                        <td>
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={() => navigate(`/contracts/${item.contractId}`)}
                                                    className="text-blue-600 hover:text-blue-900"
                                                    title="계약 상세 보기"
                                                >
                                                    <Eye size={16} />
                                                </button>
                                                <button 
                                                    onClick={() => navigate(`/contracts/edit/${item.contractId}`)}
                                                    className="text-green-600 hover:text-green-900"
                                                    title="비용 수정"
                                                >
                                                    <Edit size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Costs;